import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';

/**
 * Merges two objects, keeping only the fields present in the initial object.
 * For nested objects, recursively maintains the shape of the initial object.
 *
 * @param initial - The template object that defines the shape of the result
 * @param source - The source object to merge values from
 * @returns A new object with the shape of initial but values from source where available
 */
export function mergeKeepShape<T>(initial: T, source: unknown): T {
  // null/undefined templates become undefined
  if (initial === null || initial === undefined) {
    return undefined as T;
  }

  // Arrays: map each template element to corresponding source element
  if (isArray(initial)) {
    if (!isArray(source)) {
      return initial;
    }

    // Check if array contains objects (not just primitives)
    const hasObjects = initial.some((el) => isPlainObject(el));

    if (hasObjects) {
      // Recursively merge each element by index
      return initial.map((template, i) =>
        mergeKeepShape(template, source[i]),
      ) as T;
    }

    // For primitive arrays, replace with source
    return source as T;
  }

  // Objects: iterate keys and recurse
  if (isPlainObject(initial)) {
    const result: Record<string, unknown> = {};

    for (const key in initial) {
      const initialValue = initial[key];
      const sourceValue = isPlainObject(source)
        ? (source as Record<string, unknown>)[key]
        : undefined;

      result[key] = mergeKeepShape(initialValue, sourceValue);
    }

    return result as T;
  }

  // Primitives: use source if available, otherwise initial
  return (source !== undefined ? source : initial) as T;
}
